import React, { useState, useEffect, useCallback, useMemo, useRef } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Users, UserPlus, User, Mail, Phone, CheckCircle, XCircle, Plus, KeyRound, Search, FileDown, FileUp, Download, Upload, X, ArrowUp } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { API } from "@/utils/constants";
import { hasPermission, PERMISSIONS } from "@/utils/permissions";

// Import refactored components
import { PhotoUpload } from "./PhotoUpload";
import { VirtualizedUserList } from "./VirtualizedUserList";
import { 
  formatPhoneNumber, 
  handlePhoneChange, 
  handlePhoneBlur,
  roleLabels,
  roleBadgeColors,
  positions 
} from "./userUtils";

// Scroll to top button component (same as Assets.js)
const ScrollToTopButton = () => {
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    const toggleVisibility = () => {
      // Show button when page is scrolled down 400px
      if (window.scrollY > 400) {
        setIsVisible(true);
      } else {
        setIsVisible(false);
      }
    };

    window.addEventListener('scroll', toggleVisibility);
    return () => window.removeEventListener('scroll', toggleVisibility);
  }, []);

  const scrollToTop = () => {
    window.scrollTo({
      top: 0,
      behavior: 'smooth'
    });
  };

  if (!isVisible) return null;

  return (
    <button
      onClick={scrollToTop}
      className="fixed bottom-6 right-6 z-50 w-12 h-12 bg-blue-600 hover:bg-blue-700 text-white rounded-full shadow-lg flex items-center justify-center transition-all hover:scale-110 active:scale-95"
      style={{ bottom: 'max(1.5rem, calc(env(safe-area-inset-bottom) + 0.5rem))' }}
      data-testid="btn-scroll-to-top-users"
      aria-label="Scroll to top"
    >
      <ArrowUp className="w-5 h-5" />
    </button>
  );
};

export const UserManagementSection = ({ currentUser }) => {
  const [users, setUsers] = useState([]);
  const [accessRequests, setAccessRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState("users");
  const [searchQuery, setSearchQuery] = useState("");
  const [roleFilter, setRoleFilter] = useState("all");
  const [positionFilter, setPositionFilter] = useState("all");
  const [statusFilter, setStatusFilter] = useState("all");
  
  // Permission checks
  const userRole = currentUser?.role;
  const canAddUser = hasPermission(userRole, PERMISSIONS.USER_MANAGEMENT.ADD_USER);
  const canEditUser = hasPermission(userRole, PERMISSIONS.USER_MANAGEMENT.EDIT_USER);
  const canResetPin = hasPermission(userRole, PERMISSIONS.USER_MANAGEMENT.RESET_PIN);
  const canDeactivate = hasPermission(userRole, PERMISSIONS.USER_MANAGEMENT.DEACTIVATE);
  const canActivate = hasPermission(userRole, PERMISSIONS.USER_MANAGEMENT.ACTIVATE);
  const canApproveRequest = hasPermission(userRole, PERMISSIONS.USER_MANAGEMENT.APPROVE_REQUEST);
  const canRejectRequest = hasPermission(userRole, PERMISSIONS.USER_MANAGEMENT.REJECT_REQUEST);
  const canImportExport = hasPermission(userRole, PERMISSIONS.USER_MANAGEMENT.IMPORT_EXPORT);
  
  // Dialog states
  const [showAddUserModal, setShowAddUserModal] = useState(false);
  const [showEditUserModal, setShowEditUserModal] = useState(false);
  const [showResetPinModal, setShowResetPinModal] = useState(false);
  const [showImportExportModal, setShowImportExportModal] = useState(false);
  const [showRoleDialog, setShowRoleDialog] = useState(false);
  
  // Form states
  const [newUser, setNewUser] = useState({
    first_name: "",
    last_name: "",
    email: "",
    phone: "",
    position: "",
    pin: "",
    role: "data_entry",
    photo: null,
    photo_crop: null
  });
  const [editingUser, setEditingUser] = useState(null);
  const [resetPinUser, setResetPinUser] = useState(null);
  const [newPin, setNewPin] = useState("");
  const [selectedRequest, setSelectedRequest] = useState(null);
  const [selectedRole, setSelectedRole] = useState("data_entry");
  
  // Loading states
  const [addingUser, setAddingUser] = useState(false);
  const [savingUser, setSavingUser] = useState(false);
  const [resettingPin, setResettingPin] = useState(false);
  const [importing, setImporting] = useState(false);
  const [importResults, setImportResults] = useState(null);
  
  const fileInputRef = useRef(null);

  const fetchData = useCallback(async () => {
    try {
      const [usersRes, requestsRes] = await Promise.all([
        axios.get(`${API}/users`),
        axios.get(`${API}/access-requests`)
      ]);
      // Sort users by last name
      const sortedUsers = usersRes.data.sort((a, b) => 
        (a.last_name || '').localeCompare(b.last_name || '')
      );
      setUsers(sortedUsers);
      setAccessRequests(requestsRes.data.filter(r => r.status === "pending"));
    } catch (err) {
      toast.error("Failed to load data");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Get unique positions for filter
  const uniquePositions = useMemo(() => {
    const positionsSet = new Set(users.map(u => u.position).filter(Boolean));
    return Array.from(positionsSet).sort();
  }, [users]);

  // Filter users
  const filteredUsers = useMemo(() => {
    return users.filter(user => {
      // Search filter
      const searchLower = searchQuery.toLowerCase();
      const matchesSearch = !searchQuery || 
        user.first_name?.toLowerCase().includes(searchLower) ||
        user.last_name?.toLowerCase().includes(searchLower) ||
        user.email?.toLowerCase().includes(searchLower) ||
        user.phone?.includes(searchQuery);
      
      // Role filter
      const matchesRole = roleFilter === "all" || user.role === roleFilter;
      
      // Position filter
      const matchesPosition = positionFilter === "all" || user.position === positionFilter;
      
      // Status filter
      let matchesStatus = true;
      if (statusFilter === "active") matchesStatus = user.is_active;
      else if (statusFilter === "inactive") matchesStatus = !user.is_active;
      else if (statusFilter === "incomplete") matchesStatus = user.profile_incomplete || !user.email || !user.phone || !user.photo;
      
      return matchesSearch && matchesRole && matchesPosition && matchesStatus;
    });
  }, [users, searchQuery, roleFilter, positionFilter, statusFilter]);

  const handleApprove = (request) => {
    setSelectedRequest(request);
    setSelectedRole("data_entry");
    setShowRoleDialog(true);
  };

  const confirmApproval = async () => {
    try {
      await axios.put(`${API}/access-requests/${selectedRequest.id}/approve?role=${selectedRole}&approved_by=${currentUser.first_name} ${currentUser.last_name}`);
      toast.success("User approved and created");
      setShowRoleDialog(false);
      setSelectedRequest(null);
      fetchData();
    } catch (err) {
      toast.error("Failed to approve request");
    }
  };

  const handleReject = async (requestId) => {
    if (!window.confirm("Are you sure you want to reject this request?")) return;
    try {
      await axios.put(`${API}/access-requests/${requestId}/reject?rejected_by=${currentUser.first_name} ${currentUser.last_name}`);
      toast.success("Access request rejected");
      fetchData();
    } catch (err) {
      toast.error("Failed to reject request");
    }
  };

  const handleToggleActive = async (userId) => {
    try {
      const response = await axios.put(`${API}/users/${userId}/toggle-active`);
      toast.success(response.data.message);
      fetchData();
    } catch (err) {
      toast.error("Failed to update user status");
    }
  };

  const handleDeleteUser = async (userId) => {
    if (!window.confirm("Are you sure you want to delete this user?")) return;
    try {
      await axios.delete(`${API}/users/${userId}`);
      toast.success("User deleted");
      fetchData();
    } catch (err) {
      toast.error("Failed to delete user");
    }
  };

  const handleAddUser = async () => {
    if (!newUser.first_name || !newUser.last_name || !newUser.phone || !newUser.position || newUser.pin.length !== 4) {
      toast.error("Please fill in all required fields (PIN must be 4 digits)");
      return;
    }

    setAddingUser(true);
    try {
      await axios.post(`${API}/users`, newUser);
      toast.success("User created successfully");
      setShowAddUserModal(false);
      setNewUser({
        first_name: "",
        last_name: "",
        email: "",
        phone: "",
        position: "",
        pin: "",
        role: "data_entry",
        photo: null,
        photo_crop: null
      });
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to create user");
    } finally {
      setAddingUser(false);
    }
  };

  const handleEditUser = (user) => {
    setEditingUser({
      ...user,
      first_name: user.first_name || "",
      last_name: user.last_name || "",
      email: user.email || "",
      phone: user.phone || "",
      position: user.position || "",
      role: user.role || "data_entry",
      photo: user.photo || null,
      photo_crop: user.photo_crop || null,
      newPhoto: null,
      newPhotoCrop: null
    });
    setShowEditUserModal(true);
  };

  const handleSaveUser = async () => {
    if (!editingUser.first_name || !editingUser.last_name || !editingUser.phone) {
      toast.error("Please fill in all required fields");
      return;
    }

    setSavingUser(true);
    try {
      const updateData = {
        first_name: editingUser.first_name,
        last_name: editingUser.last_name,
        email: editingUser.email,
        phone: editingUser.phone,
        position: editingUser.position,
        role: editingUser.role
      };
      
      if (editingUser.photoDeleted) {
        updateData.photo = null;
        updateData.photo_crop = null;
      } else if (editingUser.newPhoto !== undefined) {
        updateData.photo = editingUser.newPhoto;
        updateData.photo_crop = editingUser.newPhotoCrop;
      }
      
      await axios.put(`${API}/users/${editingUser.id}`, updateData);
      toast.success("User updated successfully");
      setShowEditUserModal(false);
      setEditingUser(null);
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to update user");
    } finally {
      setSavingUser(false);
    }
  };

  const handleResetPin = (user) => {
    setResetPinUser(user);
    setNewPin("");
    setShowResetPinModal(true);
  };

  const handleConfirmResetPin = async () => {
    if (newPin.length !== 4) {
      toast.error("PIN must be 4 digits");
      return;
    }

    setResettingPin(true);
    try {
      await axios.put(`${API}/users/${resetPinUser.id}/reset-pin`, { new_pin: newPin });
      toast.success("PIN reset successfully");
      setShowResetPinModal(false);
      setResetPinUser(null);
      setNewPin("");
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to reset PIN");
    } finally {
      setResettingPin(false);
    }
  };

  const handleExportUsers = () => {
    window.open(`${API}/users/export/csv`, '_blank');
    toast.success("Downloading users CSV...");
  };

  const handleDownloadTemplate = () => {
    window.open(`${API}/users/template/csv`, '_blank');
    toast.success("Downloading template...");
  };

  const handleFileSelect = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (!file.name.endsWith('.csv')) {
      toast.error("Please select a CSV file");
      return;
    }

    setImporting(true);
    setImportResults(null);

    const formData = new FormData();
    formData.append('file', file);

    try {
      const response = await axios.post(`${API}/users/import/csv`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      setImportResults(response.data);
      if (response.data.imported_count > 0) {
        toast.success(`Successfully imported ${response.data.imported_count} users`);
        fetchData();
      } else if (response.data.errors?.length > 0) {
        toast.error("Import completed with errors");
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to import users");
      setImportResults({ errors: [err.response?.data?.detail || "Import failed"] });
    } finally {
      setImporting(false);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  const pendingRequests = accessRequests.filter(r => r.status === "pending");

  return (
    <div className="p-4 sm:p-6 lg:p-8 pt-16 sm:pt-6 lg:pt-8">
      {/* Header */}
      <div className="mb-4">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-4">
          <div>
            <h1 className="text-2xl sm:text-3xl font-bold text-slate-900">User Management</h1>
            <p className="text-slate-500 mt-1">Manage users and access requests</p>
          </div>
          <div className="flex gap-2">
            {canImportExport && (
              <Button 
                variant="outline"
                onClick={() => setShowImportExportModal(true)}
                data-testid="btn-import-export-users"
              >
                <FileDown className="w-4 h-4 mr-2" /> Import / Export
              </Button>
            )}
            {canAddUser && (
              <Button 
                onClick={() => setShowAddUserModal(true)} 
                className="bg-blue-600 hover:bg-blue-700"
                data-testid="btn-add-user"
              >
                <Plus className="w-4 h-4 mr-2" /> Add New User
              </Button>
            )}
          </div>
        </div>

        {/* Tabs */}
        <div className="bg-white rounded-t-xl border border-b-0 border-slate-200">
          <div className="flex border-b border-slate-200">
            <button
              onClick={() => setActiveTab("users")}
              className={`flex-1 px-4 py-3 text-sm font-medium ${activeTab === "users" ? "text-blue-600 border-b-2 border-blue-600" : "text-slate-500 hover:text-slate-700"}`}
            >
              <Users className="w-4 h-4 inline mr-2" />
              Users ({users.length})
            </button>
            <button
              onClick={() => setActiveTab("requests")}
              className={`flex-1 px-4 py-3 text-sm font-medium relative ${activeTab === "requests" ? "text-blue-600 border-b-2 border-blue-600" : "text-slate-500 hover:text-slate-700"}`}
            >
              <UserPlus className="w-4 h-4 inline mr-2" />
              Access Requests
              {pendingRequests.length > 0 && (
                <span className="absolute top-2 right-4 w-5 h-5 bg-red-500 text-white text-xs rounded-full flex items-center justify-center">
                  {pendingRequests.length}
                </span>
              )}
            </button>
          </div>

          {/* Search and Filters - Only show in users tab */}
          {activeTab === "users" && (
            <div className="p-4 pb-0 space-y-3">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                <Input
                  placeholder="Search users..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-10"
                  data-testid="input-search-users"
                />
              </div>
              
              <div className="flex flex-wrap gap-2 items-center">
                <span className="text-xs text-slate-500 font-medium mr-1">Filters:</span>
                
                <Select value={roleFilter} onValueChange={setRoleFilter}>
                  <SelectTrigger className="h-8 w-auto min-w-[100px] text-xs" data-testid="filter-role">
                    <SelectValue placeholder="All Roles" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Roles</SelectItem>
                    <SelectItem value="master_admin">Master Admin</SelectItem>
                    <SelectItem value="admin">Admin</SelectItem>
                    <SelectItem value="director">Director</SelectItem>
                    <SelectItem value="dispatch">Dispatch</SelectItem>
                    <SelectItem value="technician">Technician</SelectItem>
                    <SelectItem value="yard_checker">Yard Checker</SelectItem>
                    <SelectItem value="yard_goat_operator">Yard-goat Operator</SelectItem>
                    <SelectItem value="driver">Driver</SelectItem>
                    <SelectItem value="data_entry">Data Entry</SelectItem>
                    <SelectItem value="office_clerk">Office Clerk</SelectItem>
                  </SelectContent>
                </Select>
                
                <Select value={positionFilter} onValueChange={setPositionFilter}>
                  <SelectTrigger className="h-8 w-auto min-w-[120px] text-xs" data-testid="filter-position">
                    <SelectValue placeholder="All Positions" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Positions</SelectItem>
                    {uniquePositions.map(position => (
                      <SelectItem key={position} value={position}>{position}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                
                <Select value={statusFilter} onValueChange={setStatusFilter}>
                  <SelectTrigger className="h-8 w-auto min-w-[100px] text-xs" data-testid="filter-status">
                    <SelectValue placeholder="All Status" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Status</SelectItem>
                    <SelectItem value="active">Active</SelectItem>
                    <SelectItem value="inactive">Inactive</SelectItem>
                    <SelectItem value="incomplete">Needs Attention</SelectItem>
                  </SelectContent>
                </Select>
                
                {(roleFilter !== "all" || positionFilter !== "all" || statusFilter !== "all") && (
                  <Button 
                    variant="ghost" 
                    size="sm" 
                    className="h-8 text-xs text-slate-500 hover:text-slate-700"
                    onClick={() => {
                      setRoleFilter("all");
                      setPositionFilter("all");
                      setStatusFilter("all");
                    }}
                    data-testid="btn-clear-filters"
                  >
                    <X className="w-3 h-3 mr-1" /> Clear
                  </Button>
                )}
              </div>
              
              {(roleFilter !== "all" || positionFilter !== "all" || statusFilter !== "all" || searchQuery) && (
                <div className="text-xs text-slate-500">
                  Showing {filteredUsers.length} of {users.length} users
                </div>
              )}
            </div>
          )}
        </div>

        {/* Content */}
        <div className="bg-white rounded-b-xl border border-t-0 border-slate-200">
          {/* Users Tab Content */}
          {activeTab === "users" && (
            <div className="p-4">
              {loading ? (
                <div className="text-center py-8 text-slate-500">Loading...</div>
              ) : filteredUsers.length === 0 ? (
                <div className="text-center py-8 text-slate-500">
                  <Users className="w-12 h-12 mx-auto mb-3 opacity-50" />
                  <p>{searchQuery ? "No users found matching your search" : "No users yet"}</p>
                </div>
              ) : (
                <VirtualizedUserList
                  users={filteredUsers}
                  currentUser={currentUser}
                  roleLabels={roleLabels}
                  roleBadgeColors={roleBadgeColors}
                  onEdit={handleEditUser}
                  onResetPin={handleResetPin}
                  onToggleActive={handleToggleActive}
                  onDelete={handleDeleteUser}
                  canEdit={canEditUser}
                  canResetPin={canResetPin}
                  canToggleActive={canDeactivate || canActivate}
                />
              )}
            </div>
          )}

          {/* Access Requests Tab Content */}
          {activeTab === "requests" && (
            <div className="p-4">
              {loading ? (
                <div className="text-center py-8 text-slate-500">Loading...</div>
              ) : accessRequests.length === 0 ? (
                <div className="text-center py-8 text-slate-500">
                  <UserPlus className="w-12 h-12 mx-auto mb-3 opacity-50" />
                  <p>No pending access requests</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {accessRequests.map((request) => (
                    <div key={request.id} className="p-4 rounded-lg border border-yellow-200 bg-yellow-50">
                      <div className="flex items-start gap-3">
                        {request.photo ? (
                          <img src={request.photo} alt={request.first_name} className="w-10 h-10 sm:w-12 sm:h-12 rounded-full object-cover flex-shrink-0" />
                        ) : (
                          <div className="w-10 h-10 sm:w-12 sm:h-12 rounded-full bg-slate-200 flex items-center justify-center flex-shrink-0">
                            <User className="w-5 h-5 sm:w-6 sm:h-6 text-slate-400" />
                          </div>
                        )}
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 flex-wrap">
                            <span className="font-semibold text-slate-900 text-sm sm:text-base">{request.first_name} {request.last_name}</span>
                            <span className="text-xs px-2 py-0.5 rounded-full font-medium bg-yellow-100 text-yellow-700">
                              PENDING
                            </span>
                          </div>
                          <div className="text-xs sm:text-sm text-slate-600">{request.position}</div>
                          <div className="flex flex-col sm:flex-row sm:items-center gap-1 sm:gap-4 text-xs text-slate-500 mt-1">
                            <span className="flex items-center gap-1 truncate"><Mail className="w-3 h-3 flex-shrink-0" /><span className="truncate">{request.email}</span></span>
                            <span className="flex items-center gap-1"><Phone className="w-3 h-3 flex-shrink-0" />{request.phone}</span>
                          </div>
                          <div className="text-xs text-slate-400 mt-1">
                            Submitted {new Date(request.created_at).toLocaleString()}
                          </div>
                        </div>
                      </div>
                      
                      <div className="flex items-center gap-2 mt-3 pt-3 border-t border-yellow-200">
                        <Button
                          size="sm"
                          onClick={() => handleApprove(request)}
                          className="bg-green-600 hover:bg-green-700 text-white flex-1 sm:flex-none"
                        >
                          <CheckCircle className="w-4 h-4 mr-1" />
                          Approve
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => handleReject(request.id)}
                          className="text-red-600 border-red-200 hover:bg-red-50 flex-1 sm:flex-none"
                        >
                          <XCircle className="w-4 h-4 mr-1" />
                          Reject
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Role Selection Dialog */}
      <Dialog open={showRoleDialog} onOpenChange={setShowRoleDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Assign Role</DialogTitle>
          </DialogHeader>
          <div className="py-4">
            <p className="text-sm text-slate-600 mb-4">
              Approving access for <strong>{selectedRequest?.first_name} {selectedRequest?.last_name}</strong>
            </p>
            <Label>Select Role</Label>
            <Select value={selectedRole} onValueChange={setSelectedRole}>
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="master_admin">Master Admin</SelectItem>
                <SelectItem value="admin">Admin</SelectItem>
                <SelectItem value="supervisor">Supervisor</SelectItem>
                <SelectItem value="dispatch">Dispatch</SelectItem>
                <SelectItem value="data_entry">Data Entry</SelectItem>
                <SelectItem value="technician">Technician</SelectItem>
                <SelectItem value="yard_goat_operator">Yard-goat Operator</SelectItem>
                <SelectItem value="office_clerk">Office Clerk</SelectItem>
                <SelectItem value="driver">Driver</SelectItem>
                <SelectItem value="director">Director</SelectItem>
                <SelectItem value="yard_checker">Yard Checker</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="flex gap-3 justify-end">
            <Button variant="outline" onClick={() => setShowRoleDialog(false)}>Cancel</Button>
            <Button onClick={confirmApproval} className="bg-green-600 hover:bg-green-700">
              Approve & Create User
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Add New User Dialog */}
      <Dialog open={showAddUserModal} onOpenChange={setShowAddUserModal}>
        <DialogContent className="w-screen h-[100dvh] max-w-none max-h-[100dvh] m-0 rounded-none flex flex-col fixed inset-0 p-0 overflow-hidden">
          <DialogHeader className="flex-shrink-0 p-4 border-b bg-white">
            <DialogTitle className="flex items-center gap-2">
              <UserPlus className="w-5 h-5 text-blue-600" />
              Add New User
            </DialogTitle>
          </DialogHeader>
          <div className="flex-1 overflow-y-auto p-4" style={{ WebkitOverflowScrolling: 'touch' }}>
            <div className="max-w-lg mx-auto space-y-4">
              <div className="flex justify-center pb-4 border-b border-slate-100">
                <PhotoUpload 
                  photo={newUser.photo}
                  cropPosition={newUser.photo_crop}
                  onPhotoChange={(photo, cropData) => setNewUser({ ...newUser, photo, photo_crop: cropData })}
                />
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label htmlFor="new-first-name">First Name *</Label>
                  <Input
                    id="new-first-name"
                    value={newUser.first_name}
                    onChange={(e) => setNewUser({ ...newUser, first_name: e.target.value })}
                    placeholder="First Name"
                    className="h-11 mt-1"
                    data-testid="input-new-first-name"
                  />
                </div>
                <div>
                  <Label htmlFor="new-last-name">Last Name *</Label>
                  <Input
                    id="new-last-name"
                    value={newUser.last_name}
                    onChange={(e) => setNewUser({ ...newUser, last_name: e.target.value })}
                    placeholder="Last Name"
                    className="h-11 mt-1"
                    data-testid="input-new-last-name"
                  />
                </div>
              </div>

              <div>
                <Label htmlFor="new-email">Email</Label>
                <Input
                  id="new-email"
                  type="email"
                  value={newUser.email}
                  onChange={(e) => setNewUser({ ...newUser, email: e.target.value })}
                  placeholder="employee@pacificexpressway.com (optional)"
                  className="h-11 mt-1"
                  data-testid="input-new-email"
                />
              </div>

              <div>
                <Label htmlFor="new-phone">Phone Number *</Label>
                <Input
                  id="new-phone"
                  type="tel"
                  value={formatPhoneNumber(newUser.phone)}
                  onChange={(e) => handlePhoneChange(e.target.value, setNewUser)}
                  onBlur={() => handlePhoneBlur(newUser.phone, setNewUser)}
                  placeholder="(555) 123-4567"
                  className="h-11 mt-1"
                  data-testid="input-new-phone"
                />
              </div>

              <div>
                <Label>Position *</Label>
                <Select 
                  value={newUser.position} 
                  onValueChange={(value) => setNewUser({ ...newUser, position: value })}
                >
                  <SelectTrigger data-testid="select-new-position" className="h-11 mt-1">
                    <SelectValue placeholder="Select a position..." />
                  </SelectTrigger>
                  <SelectContent>
                    {positions.map((pos) => (
                      <SelectItem key={pos.value} value={pos.label}>{pos.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div>
                <Label>Role *</Label>
                <Select 
                  value={newUser.role} 
                  onValueChange={(value) => setNewUser({ ...newUser, role: value })}
                >
                  <SelectTrigger data-testid="select-new-role" className="h-11 mt-1">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="master_admin">Master Admin</SelectItem>
                    <SelectItem value="admin">Admin</SelectItem>
                    <SelectItem value="supervisor">Supervisor</SelectItem>
                    <SelectItem value="dispatch">Dispatch</SelectItem>
                    <SelectItem value="data_entry">Data Entry</SelectItem>
                    <SelectItem value="technician">Technician</SelectItem>
                    <SelectItem value="yard_goat_operator">Yard-goat Operator</SelectItem>
                    <SelectItem value="office_clerk">Office Clerk</SelectItem>
                    <SelectItem value="driver">Driver</SelectItem>
                    <SelectItem value="director">Director</SelectItem>
                    <SelectItem value="yard_checker">Yard Checker</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div>
                <Label htmlFor="new-pin">PIN (4 digits) *</Label>
                <Input
                  id="new-pin"
                  type="text"
                  inputMode="numeric"
                  maxLength={4}
                  value={newUser.pin}
                  onChange={(e) => {
                    const value = e.target.value.replace(/\D/g, '').slice(0, 4);
                    setNewUser({ ...newUser, pin: value });
                  }}
                  placeholder="Enter 4-digit PIN"
                  className="h-11 mt-1"
                  data-testid="input-new-pin"
                />
              </div>
            </div>
          </div>
          <div className="flex-shrink-0 flex items-center justify-between p-4 border-t bg-white" style={{ paddingBottom: 'max(1rem, env(safe-area-inset-bottom))' }}>
            <Button variant="outline" onClick={() => setShowAddUserModal(false)} className="h-10 px-4">
              Cancel
            </Button>
            <Button 
              onClick={handleAddUser} 
              disabled={addingUser}
              className="bg-blue-600 hover:bg-blue-700 h-10 px-4"
              data-testid="btn-create-user"
            >
              {addingUser ? "Creating..." : "Create User"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Edit User Dialog */}
      <Dialog open={showEditUserModal} onOpenChange={setShowEditUserModal}>
        <DialogContent className="w-screen h-[100dvh] max-w-none max-h-[100dvh] m-0 rounded-none flex flex-col fixed inset-0 p-0 overflow-hidden">
          <DialogHeader className="flex-shrink-0 p-4 border-b bg-white">
            <DialogTitle className="flex items-center gap-2">
              <User className="w-5 h-5 text-blue-600" />
              Edit User
            </DialogTitle>
          </DialogHeader>
          <div className="flex-1 overflow-y-auto p-4" style={{ WebkitOverflowScrolling: 'touch' }}>
            <div className="max-w-lg mx-auto space-y-4">
              <div className="flex justify-center pb-4 border-b border-slate-100">
                <PhotoUpload 
                  photo={editingUser?.newPhoto !== undefined ? editingUser.newPhoto : null}
                  cropPosition={editingUser?.newPhotoCrop !== undefined ? editingUser.newPhotoCrop : null}
                  existingPhoto={editingUser?.photo}
                  existingCropPosition={editingUser?.photo_crop}
                  onPhotoChange={(photo, cropData) => {
                    if (photo === null && cropData === null) {
                      setEditingUser({ 
                        ...editingUser, 
                        newPhoto: null, 
                        newPhotoCrop: null,
                        photo: null,
                        photo_crop: null,
                        photoDeleted: true
                      });
                    } else {
                      setEditingUser({ 
                        ...editingUser, 
                        newPhoto: photo, 
                        newPhotoCrop: cropData,
                        photoDeleted: false 
                      });
                    }
                  }}
                />
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label htmlFor="edit-first-name">First Name *</Label>
                  <Input
                    id="edit-first-name"
                    value={editingUser?.first_name || ""}
                    onChange={(e) => setEditingUser({ ...editingUser, first_name: e.target.value })}
                    placeholder="First Name"
                    className="h-11 mt-1"
                    data-testid="input-edit-first-name"
                  />
                </div>
                <div>
                  <Label htmlFor="edit-last-name">Last Name *</Label>
                  <Input
                    id="edit-last-name"
                    value={editingUser?.last_name || ""}
                    onChange={(e) => setEditingUser({ ...editingUser, last_name: e.target.value })}
                    placeholder="Last Name"
                    className="h-11 mt-1"
                    data-testid="input-edit-last-name"
                  />
                </div>
              </div>

              <div>
                <Label htmlFor="edit-email">Email</Label>
                <Input
                  id="edit-email"
                  type="email"
                  value={editingUser?.email || ""}
                  onChange={(e) => setEditingUser({ ...editingUser, email: e.target.value })}
                  placeholder="employee@pacificexpressway.com (optional)"
                  className="h-11 mt-1"
                  data-testid="input-edit-email"
                />
              </div>

              <div>
                <Label htmlFor="edit-phone">Phone Number *</Label>
                <Input
                  id="edit-phone"
                  type="tel"
                  value={formatPhoneNumber(editingUser?.phone || "")}
                  onChange={(e) => handlePhoneChange(e.target.value, setEditingUser)}
                  onBlur={(e) => handlePhoneBlur(editingUser?.phone, setEditingUser)}
                  placeholder="(555) 123-4567"
                  className="h-11 mt-1"
                  data-testid="input-edit-phone"
                />
              </div>

              <div>
                <Label>Position</Label>
                <Select 
                  value={editingUser?.position || ""} 
                  onValueChange={(value) => setEditingUser({ ...editingUser, position: value })}
                >
                  <SelectTrigger data-testid="select-edit-position" className="h-11 mt-1">
                    <SelectValue placeholder="Select a position..." />
                  </SelectTrigger>
                  <SelectContent>
                    {positions.map((pos) => (
                      <SelectItem key={pos.value} value={pos.label}>{pos.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div>
                <Label>Role</Label>
                <Select 
                  value={editingUser?.role || "data_entry"} 
                  onValueChange={(value) => setEditingUser({ ...editingUser, role: value })}
                >
                  <SelectTrigger data-testid="select-edit-role" className="h-11 mt-1">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="master_admin">Master Admin</SelectItem>
                    <SelectItem value="admin">Admin</SelectItem>
                    <SelectItem value="supervisor">Supervisor</SelectItem>
                    <SelectItem value="dispatch">Dispatch</SelectItem>
                    <SelectItem value="data_entry">Data Entry</SelectItem>
                    <SelectItem value="technician">Technician</SelectItem>
                    <SelectItem value="yard_goat_operator">Yard-goat Operator</SelectItem>
                    <SelectItem value="office_clerk">Office Clerk</SelectItem>
                    <SelectItem value="driver">Driver</SelectItem>
                    <SelectItem value="director">Director</SelectItem>
                    <SelectItem value="yard_checker">Yard Checker</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {/* Reset PIN Section */}
              <div className="bg-slate-50 border border-slate-200 rounded-lg p-4">
                <div className="flex items-center gap-2 mb-3">
                  <KeyRound className="w-4 h-4 text-purple-600" />
                  <Label className="text-sm font-medium">Reset PIN</Label>
                </div>
                <div className="flex gap-2">
                  <Input
                    type="text"
                    inputMode="numeric"
                    maxLength={4}
                    value={editingUser?.newPin || ""}
                    onChange={(e) => {
                      const val = e.target.value.replace(/\D/g, '').slice(0, 4);
                      setEditingUser({ ...editingUser, newPin: val });
                    }}
                    placeholder="Enter new 4-digit PIN"
                    className="h-10 flex-1"
                    data-testid="input-edit-new-pin"
                  />
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    className="h-10 px-4 text-purple-600 border-purple-200 hover:bg-purple-50"
                    disabled={!editingUser?.newPin || editingUser?.newPin?.length !== 4}
                    data-testid="btn-set-new-pin"
                    onClick={async () => {
                      try {
                        await axios.put(`${API}/users/${editingUser.id}/reset-pin`, { new_pin: editingUser.newPin });
                        toast.success("PIN updated successfully");
                        setEditingUser({ ...editingUser, newPin: "", pin: editingUser.newPin });
                        fetchData(); // Refresh user list
                      } catch (err) {
                        toast.error(err.response?.data?.detail || "Failed to update PIN");
                      }
                    }}
                  >
                    Set PIN
                  </Button>
                </div>
                <p className="text-xs text-slate-500 mt-2">
                  Current PIN will be replaced. PIN must be exactly 4 digits.
                </p>
              </div>
            </div>
          </div>
          <div className="flex-shrink-0 flex items-center justify-between p-4 border-t bg-white" style={{ paddingBottom: 'max(1rem, env(safe-area-inset-bottom))' }}>
            <Button variant="outline" onClick={() => { setShowEditUserModal(false); setEditingUser(null); }} className="h-10 px-4">
              Cancel
            </Button>
            <Button 
              onClick={handleSaveUser} 
              disabled={savingUser}
              className="bg-blue-600 hover:bg-blue-700 h-10 px-4"
              data-testid="btn-save-user"
            >
              {savingUser ? "Saving..." : "Save Changes"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Reset PIN Dialog */}
      <Dialog open={showResetPinModal} onOpenChange={setShowResetPinModal}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <KeyRound className="w-5 h-5 text-purple-600" />
              Reset PIN
            </DialogTitle>
          </DialogHeader>
          <div className="py-6">
            <div className="bg-slate-50 rounded-lg p-4 mb-6">
              <p className="text-sm text-slate-600">
                Resetting PIN for: <strong>{resetPinUser?.first_name} {resetPinUser?.last_name}</strong>
              </p>
              <p className="text-xs text-slate-500 mt-1">{resetPinUser?.email}</p>
            </div>
            
            <div>
              <Label htmlFor="reset-pin">New 4-Digit PIN *</Label>
              <Input
                id="reset-pin"
                type="text"
                inputMode="numeric"
                maxLength={4}
                value={newPin}
                onChange={(e) => {
                  const value = e.target.value.replace(/\D/g, '').slice(0, 4);
                  setNewPin(value);
                }}
                placeholder="Enter new PIN"
                className="h-14 text-center text-2xl tracking-widest mt-2"
                data-testid="input-reset-pin"
              />
              <p className="text-sm text-slate-500 mt-3">
                Enter a new 4-digit PIN for this user. They will use this to log in.
              </p>
            </div>
          </div>
          <div className="flex gap-3 justify-end">
            <Button variant="outline" onClick={() => { setShowResetPinModal(false); setResetPinUser(null); setNewPin(""); }}>
              Cancel
            </Button>
            <Button 
              onClick={handleConfirmResetPin} 
              disabled={resettingPin || newPin.length !== 4}
              className="bg-purple-600 hover:bg-purple-700"
              data-testid="btn-confirm-reset-pin"
            >
              {resettingPin ? "Resetting..." : "Reset PIN"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Import/Export Dialog */}
      <Dialog open={showImportExportModal} onOpenChange={(open) => { setShowImportExportModal(open); if (!open) setImportResults(null); }}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <FileDown className="w-5 h-5 text-blue-600" />
              Import / Export Users
            </DialogTitle>
          </DialogHeader>
          <div className="py-4 space-y-6">
            <div className="space-y-3">
              <h3 className="font-semibold text-slate-900 flex items-center gap-2">
                <Download className="w-4 h-4" /> Export Users
              </h3>
              <p className="text-sm text-slate-500">Download all users as a CSV file.</p>
              <Button 
                variant="outline" 
                onClick={handleExportUsers}
                className="w-full"
                data-testid="btn-export-users"
              >
                <FileDown className="w-4 h-4 mr-2" /> Export Users to CSV
              </Button>
            </div>

            <div className="border-t border-slate-200" />

            <div className="space-y-3">
              <h3 className="font-semibold text-slate-900 flex items-center gap-2">
                <Upload className="w-4 h-4" /> Import Users
              </h3>
              <p className="text-sm text-slate-500">Upload a CSV file to bulk import users. Download the template first to see the required format.</p>
              
              <div className="flex gap-2">
                <Button 
                  variant="outline" 
                  onClick={handleDownloadTemplate}
                  className="flex-1"
                  data-testid="btn-download-template"
                >
                  <FileDown className="w-4 h-4 mr-2" /> Download Template
                </Button>
                <Button 
                  variant="outline"
                  onClick={() => fileInputRef.current?.click()}
                  disabled={importing}
                  className="flex-1"
                  data-testid="btn-import-users"
                >
                  <FileUp className="w-4 h-4 mr-2" /> {importing ? "Importing..." : "Import CSV"}
                </Button>
                <input
                  type="file"
                  ref={fileInputRef}
                  accept=".csv"
                  onChange={handleFileSelect}
                  className="hidden"
                />
              </div>

              {importResults && (
                <div className={`p-4 rounded-lg ${importResults.imported_count > 0 ? 'bg-green-50 border border-green-200' : 'bg-red-50 border border-red-200'}`}>
                  <p className={`font-medium ${importResults.imported_count > 0 ? 'text-green-700' : 'text-red-700'}`}>
                    {importResults.imported_count > 0 
                      ? `Successfully imported ${importResults.imported_count} users`
                      : 'No users imported'}
                    {importResults.skipped_count > 0 && `, ${importResults.skipped_count} skipped`}
                  </p>
                  {importResults.errors?.length > 0 && (
                    <div className="mt-2 max-h-32 overflow-y-auto">
                      <p className="text-sm text-red-600 font-medium">Errors:</p>
                      <ul className="text-xs text-red-600 list-disc pl-4 mt-1">
                        {importResults.errors.map((err, i) => (
                          <li key={i}>{err}</li>
                        ))}
                      </ul>
                    </div>
                  )}
                </div>
              )}

              <div className="bg-slate-50 rounded-lg p-3">
                <p className="text-xs text-slate-600 font-medium mb-1">CSV Format:</p>
                <p className="text-xs text-slate-500">first_name, last_name, email, phone, position, role, pin, is_active</p>
                <p className="text-xs text-slate-400 mt-1">Valid roles: admin, dispatch, data_entry, technician, yard_goat_operator, office_clerk, driver</p>
              </div>
            </div>
          </div>
          <div className="flex justify-end pt-2 border-t">
            <Button variant="outline" onClick={() => { setShowImportExportModal(false); setImportResults(null); }}>
              Close
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Scroll to Top Button */}
      <ScrollToTopButton />
    </div>
  );
};
